import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { FileText, ChevronDown, ChevronUp, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { getChildLastActivity } from '../shared/childActivity';
import { DataFreshness } from '../../shared/DataFreshness';
import { getAssessmentHistory, getReport, type AssessmentReport, type AssessmentHistoryEntry } from '../../../assessment';
import { parseGroupNum } from '../shared/groupUtils';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getModuleIcon, getModuleColors } from '../../../content';
interface ToetsenPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  setViewingLearner: (id: string) => void;
}
export function ToetsenPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  setViewingLearner
}: ToetsenPageProps) {
  const [history, setHistory] = useState<{
    entry: AssessmentHistoryEntry;
    report: AssessmentReport;
  }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!selectedChild) {
      setHistory([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const groupNum = parseGroupNum(selectedGroup);
    getAssessmentHistory(groupNum).then(async allHistory => {
      if (cancelled) return;
      const childHistory = allHistory.filter(entry => entry.learnerId === selectedChild.id).sort((a, b) => b.completedAt - a.completedAt);
      const fullHistory = [];
      for (const entry of childHistory) {
        const report = await getReport(entry.attemptId);
        if (report) {
          fullHistory.push({
            entry,
            report
          });
        }
      }
      if (!cancelled) {
        setHistory(fullHistory);
        setIsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setHistory([]);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedChild?.id, selectedGroup]);
  if (!selectedChild) {
    return <PageLayout title="Toetsen" description="Bekijk de toetshistorie" data-id="element-8325">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-8326">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const latestReport = history.length > 0 ? history[0].report : null;
  return <PageLayout title="Toetsen" description={`Bekijk de toetshistorie van ${selectedChild.displayName}`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-8328" />} data-id="element-8327">
      <div className="max-w-4xl mx-auto space-y-8 pb-12" data-id="element-8329">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8330" />

        {isLoading ? <div className="space-y-4" data-id="element-8331">
            <Skeleton className="h-40 w-full rounded-2xl" data-id="element-8332" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8333" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8334" />
          </div> : history.length === 0 ? <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed border-2 border-slate-200 bg-slate-50/50" data-id="element-8335">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 mb-6" data-id="element-8336">
              <DjayNarrator size="lg" emotion="thinking" data-id="element-8337" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8338">
              Nog geen toetsen gemaakt
            </h3>
            <p className="text-slate-500 max-w-md" data-id="element-8339">
              Zodra {selectedChild.displayName} een niveaubepaling of toets
              afrondt, verschijnt hier de historie en het geschatte CITO-niveau.
            </p>
          </Card> : <div className="space-y-8" data-id="element-8340">
            {/* Hero: Latest Assessment */}
            {latestReport && <section data-id="element-8341">
                <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8342">
                  Laatste Resultaat
                </h2>
                <Card className="p-6 sm:p-8 border-emerald-200 bg-emerald-50/50 shadow-sm" data-id="element-8343">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-8344">
                    <div className="flex items-center gap-5" data-id="element-8345">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white border border-emerald-100 shadow-sm text-emerald-600" data-id="element-8346">
                        <FileText className="w-8 h-8" data-id="element-8347" />
                      </div>
                      <div data-id="element-8348">
                        <div className="flex items-center gap-2 mb-1" data-id="element-8349">
                          <span className="text-sm font-bold text-emerald-800 uppercase tracking-wider" data-id="element-8350">
                            Niveaubepaling
                          </span>
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md" data-id="element-8351">
                            Groep {latestReport.selectedGroup}
                          </span>
                        </div>
                        <div className="text-2xl font-black text-slate-900" data-id="element-8352">
                          {new Date(latestReport.completedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 self-start sm:self-center bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm" data-id="element-8353">
                      <div className="text-right" data-id="element-8354">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-8355">
                          Geschat Niveau
                        </div>
                        <div className="text-3xl font-black text-slate-800 leading-none" data-id="element-8356">
                          {latestReport.overallEstimatedLevelCode}
                        </div>
                      </div>

                      {(() => {
                  const cito = levelCodeToCitoLevel(latestReport.overallEstimatedLevelCode, latestReport.selectedGroup);
                  if (!cito) return null;
                  return <div className={`flex items-center justify-center px-4 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8357">
                            <span className={`text-xl font-black ${cito.color}`} data-id="element-8358">
                              {cito.label}
                            </span>
                          </div>;
                })()}
                    </div>
                  </div>
                </Card>
              </section>}

            {/* History List */}
            <section data-id="element-8359">
              <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8360">
                Historie
              </h2>
              <div className="space-y-3" data-id="element-8361">
                {history.map(({
              entry,
              report
            }, index) => {
              const isExpanded = expandedId === entry.attemptId;
              const prevReport = history[index + 1]?.report;
              const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
              const panelId = `toets-panel-${entry.attemptId}`;
              return <Card key={entry.attemptId} className={`overflow-hidden transition-all duration-200 border-slate-200 ${isExpanded ? 'ring-2 ring-emerald-500 border-transparent shadow-md' : 'hover:border-emerald-300 hover:shadow-sm'}`} data-id="element-8362">
                      <div role="button" tabIndex={0} aria-expanded={isExpanded} aria-controls={panelId} onClick={() => setExpandedId(isExpanded ? null : entry.attemptId)} onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : entry.attemptId);
                  }
                }} className="p-4 sm:p-5 flex items-center justify-between gap-4 bg-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" data-id="element-8363">
                        <div className="flex items-center gap-4" data-id="element-8364">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400" data-id="element-8365">
                            <FileText className="w-5 h-5" data-id="element-8366" />
                          </div>
                          <div data-id="element-8367">
                            <div className="font-bold text-slate-900" data-id="element-8368">
                              {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-8369">
                              Groep {entry.selectedGroup} •{' '}
                              {entry.totalQuestions} vragen
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 sm:gap-6" data-id="element-8370">
                          <div className="text-right hidden sm:block" data-id="element-8371">
                            <div className="text-lg font-black text-slate-800" data-id="element-8372">
                              {report.overallEstimatedLevelCode}
                            </div>
                          </div>

                          {cito && <div className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8373">
                              <span className={`text-sm font-black ${cito.color}`} data-id="element-8374">
                                {cito.label}
                              </span>
                            </div>}

                          <div className="text-slate-400" data-id="element-8375">
                            {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-8376" /> : <ChevronDown className="w-5 h-5" data-id="element-8377" />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Sub-scores */}
                      <AnimatePresence initial={false} data-id="element-8378">
                        {isExpanded && <motion.div id={panelId} key="panel" initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="border-t border-slate-100 bg-slate-50/50 overflow-hidden" data-id="element-8379">
                            <div className="p-4 sm:p-5" data-id="element-8380">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8381">
                                Scores per vak
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8382">
                                {report.estimatedLevels.map(mod => {
                          const IconComp = getModuleIcon(mod.moduleId);
                          const colors = getModuleColors(mod.moduleId);
                          const prevMod = prevReport?.estimatedLevels.find(m => m.moduleId === mod.moduleId);
                          const delta = prevMod ? mod.estimatedTier - prevMod.estimatedTier : 0;
                          return <div key={mod.moduleId} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-8383">
                                      <div className="flex items-center gap-3" data-id="element-8384">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg} ${colors.text}`} data-id="element-8385">
                                          <IconComp className="w-4 h-4" data-id="element-8386" />
                                        </div>
                                        <div className="font-bold text-slate-700 text-sm" data-id="element-8387">
                                          {mod.moduleTitle}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3" data-id="element-8388">
                                        {prevMod && <div className="flex items-center text-xs font-bold" data-id="element-8389">
                                            {delta > 0 ? <span className="text-emerald-500 flex items-center" aria-label="Gestegen sinds vorige toets" title="Gestegen sinds vorige toets" data-id="element-8390">
                                                <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-8391" />
                                              </span> : delta < 0 ? <span className="text-rose-500 flex items-center" aria-label="Gedaald sinds vorige toets" title="Gedaald sinds vorige toets" data-id="element-8392">
                                                <TrendingDown className="w-3 h-3 mr-0.5" data-id="element-8393" />
                                              </span> : <span className="text-slate-300 flex items-center" aria-label="Gelijk gebleven" title="Gelijk gebleven" data-id="element-8394">
                                                <Minus className="w-3 h-3 mr-0.5" data-id="element-8395" />
                                              </span>}
                                          </div>}
                                        <div className="text-sm font-black text-slate-900 bg-slate-100 px-2 py-1 rounded-md" data-id="element-8396">
                                          {mod.estimatedLevelCode}
                                        </div>
                                      </div>
                                    </div>;
                        })}
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </Card>;
            })}
              </div>
            </section>

            {/* Disclaimer */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-8397">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-8398" />
              <div className="text-sm text-blue-800 leading-relaxed" data-id="element-8399">
                <strong data-id="element-8400">Let op:</strong> De getoonde niveaus zijn een
                inschatting op basis van de gemaakte opgaven in Meester Djay.
                Dit is geen officiële CITO-score en kan afwijken van de
                resultaten op school. Gebruik deze gegevens als indicatie van de
                groei.
              </div>
            </div>
          </div>}
      </div>
    </PageLayout>;
}